/**
 * Puente con el cascarón nativo de OTRIX (otrix-mobile).
 *
 * Solo se activa cuando la página corre dentro de la app: el WebView firma su user agent con
 * "OtrixApp" (applicationNameForUserAgent). En un navegador normal este módulo no hace nada.
 *
 * Qué ofrece al nativo:
 *  - window.OtrixNative.setSession(loginResponse): inicia sesión con el MISMO camino que el
 *    saga de login (saveAuthInfo → settings.find → esperar → loginSuccess), para que la app
 *    no pida credenciales dos veces. El payload es el retorno completo de la mutation login
 *    (tokens + user con settings.id incluido).
 *  - window.OtrixNative.clearSession(): limpia la sesión web cuando el nativo cierra la suya.
 *
 * Además marca el documento con data-otrix-native, por si algún estilo necesita saberlo.
 * MobileTabBar y el colchón de DefaultLayout se ocultan por su cuenta con el mismo detector:
 * dentro del cascarón la navegación la pinta la app.
 */
import { store, persistor } from 'store';
import i18n from 'i18n';
import authSlice from 'store/modules/auth';
import settingsSlice from 'store/modules/settings';
import requestStatus from 'common/constants/status';
import { saveAuthInfo, removeAuthInfo } from 'common/utils/general';

const isNativeShell =
  typeof window !== 'undefined' && /OtrixApp/.test(window.navigator.userAgent);

if (isNativeShell) {
  document.documentElement.dataset.otrixNative = 'true';

  window.OtrixNative = {
    async setSession(loginResponse) {
      try {
        if (!loginResponse?.access_token || !loginResponse?.user) return false;
        await saveAuthInfo(loginResponse);

        // El saga espera a que las settings estén cargadas antes de dar la sesión por buena;
        // aquí se replica esa espera con una suscripción y un tope para no colgarse nunca.
        // Si settings termina en ERROR (tokens rechazados por el backend), la sesión NO se da
        // por buena: se revierte la escritura y se devuelve false, para que el cascarón decida
        // con información real en vez de recibir un ok:true mentiroso segundos antes del
        // redirect fatal del interceptor. El tope de tiempo (red lenta) sí concede el login.
        const settingsId = loginResponse.user.settings?.id;
        if (settingsId) {
          const resultado = await new Promise((resolve) => {
            let timer = null;
            let unsubscribe = () => {};
            const done = (estado) => {
              unsubscribe();
              clearTimeout(timer);
              resolve(estado);
            };
            unsubscribe = store.subscribe(() => {
              const st = store.getState().settings?.status;
              if (st === requestStatus.success) done('ok');
              if (st === requestStatus.error) done('error');
            });
            timer = setTimeout(() => done('timeout'), 5000);
            store.dispatch(settingsSlice.actions.find(settingsId));
          });
          if (resultado === 'error') {
            await removeAuthInfo();
            return false;
          }
        }

        store.dispatch(authSlice.actions.loginSuccess(loginResponse));
        return true;
      } catch (ex) {
        // Un fallo aquí nunca debe romper la web: el usuario simplemente verá el login.
        return false;
      }
    },

    /**
     * Cambia el tema en caliente, sin recargar: el cascaron lo llama cuando el usuario
     * alterna claro/oscuro en la UI nativa. Persiste igual que el toggle de la web
     * (el reducer pasa por storeThemeMode, que escribe otx_theme_mode en localStorage).
     */
    setThemeMode(mode) {
      try {
        if (mode !== 'light' && mode !== 'dark') return false;
        store.dispatch(settingsSlice.actions.setThemeMode(mode));
        return true;
      } catch (ex) {
        return false;
      }
    },

    /**
     * Cambia el idioma en caliente. La web no persiste el idioma (usa el del navegador en cada
     * carga), asi que el cascaron lo empuja en cada pagina para que el portal embebido hable
     * el idioma elegido en la UI nativa.
     */
    setLanguage(lang) {
      try {
        if (lang !== 'es' && lang !== 'en') return false;
        i18n.changeLanguage(lang);
        return true;
      } catch (ex) {
        return false;
      }
    },

    async clearSession() {
      try {
        await removeAuthInfo();
        await persistor.purge();
      } catch (ex) {
        // limpiar es best-effort
      }
      window.location.replace('/login');
    }
  };

  // Si la web cierra sesión por su cuenta (botón de logout en /more, purge del interceptor),
  // el cascarón debe enterarse y cerrar la sesión NATIVA en orden: sin este aviso lo
  // interpretaba como sesión zombi e intentaba re-loguear la web, deshaciendo el logout.
  let usuarioPrevio = Boolean(store.getState().auth?.user);
  store.subscribe(() => {
    const hayUsuario = Boolean(store.getState().auth?.user);
    if (usuarioPrevio && !hayUsuario) {
      try {
        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'web-logout' }));
      } catch (ex) {
        // best-effort
      }
    }
    usuarioPrevio = hayUsuario;
  });

  // Aviso al nativo de que el puente existe (llega por onMessage si está escuchando).
  window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'bridge', ready: true }));
}

export default isNativeShell;
